import type { ReactNode } from "react";
import { X } from "lucide-react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/utils/helpers";
import { kitButtonClass } from "../../components/kit/Button";
import { KitCheckbox } from "../../components/kit/Checkbox";
import { KitIconButton } from "../../components/kit/IconButton";
import { KitModal } from "../../components/kit/Modal";

export type CalendarViewport = "phone" | "tablet" | "desktop";

const PHONE = "(max-width: 767px)";
const TABLET = "(min-width: 768px) and (max-width: 1023px)";

/** Phone below 768, tablet to 1023 (DESIGN.md "The calendar on a tablet"), desktop above. */
export function useCalendarViewport(): CalendarViewport {
  const phone = useMediaQuery(PHONE);
  const tablet = useMediaQuery(TABLET);
  return phone ? "phone" : tablet ? "tablet" : "desktop";
}

/** The phone sheet's handle: 36 x 4 at radius 2 in faint, centred in an 18px row (Calendar / Move · Phone). */
export function SheetGrabber() {
  return (
    <span aria-hidden="true" className="flex h-[18px] shrink-0 items-center justify-center">
      <span className="h-1 w-9 rounded-[2px] bg-co-faint" />
    </span>
  );
}

/**
 * A calendar pop-up (style B): floating chrome at radius 16 with no hairline,
 * centred on a desktop or a tablet, and on a phone a sheet floating 8px in from
 * the sides and the foot. Pegs are retired. The caller draws its own header.
 * `flush` drops the dialog's own padding for callers that pad each band
 * themselves, as the Pen's Dialog / Confirm does.
 */
export function CalendarSheet({
  phone,
  label,
  onClose,
  dismissable = true,
  width = "max-w-[560px]",
  large = false,
  grabber = true,
  flush = false,
  children,
}: {
  phone: boolean;
  /** The dialog's accessible name. */
  label: string;
  onClose: () => void;
  /** False while something is saving, so a stray tap outside does not drop it. */
  dismissable?: boolean;
  /** The desktop width, as a Tailwind max-width class. */
  width?: string;
  /** Phone only: the sheet takes the screen's height, less its 8px. */
  large?: boolean;
  /** Phone only: the handle at the top (sheets have one; confirms do not). */
  grabber?: boolean;
  flush?: boolean;
  children: ReactNode;
}) {
  return (
    <KitModal
      isOpen
      hideCloseButton
      aria-label={label}
      isDismissable={dismissable}
      onOpenChange={(open) => !open && dismissable && onClose()}
      overlayClassName={phone ? "items-end p-2" : undefined}
      modalClassName={cn(
        phone
          ? cn(
              "min-w-0 max-w-none",
              flush ? "p-0" : cn("px-4 pb-4", grabber ? "pt-0" : "pt-5"),
              large ? "h-[calc(100dvh-16px)] max-h-none" : "max-h-[calc(100dvh-16px)]",
            )
          : cn(width, flush && "p-0"),
      )}
    >
      {phone && grabber ? <SheetGrabber /> : null}
      {children}
    </KitModal>
  );
}

/**
 * Dialog / Confirm (Fea6g, I9s0B; Sheet / Confirm · Phone, epW6O, NilVL): the
 * question in Barlow Condensed 20/800, one muted line under it, the close X,
 * then the consequence rows, then Cancel as an accent text capsule and the verb
 * as the one filled capsule. On a phone it is the same card 8px in from the
 * sides and the foot, with two 48px capsules sharing the width.
 */
export function CalendarConfirm({
  phone,
  title,
  description,
  onClose,
  busy = false,
  width = "max-w-[520px]",
  cancelLabel,
  confirmLabel,
  onConfirm,
  confirmDisabled = false,
  tone = "danger",
  label,
  children,
}: {
  phone: boolean;
  title: string;
  /** The dialog's accessible name, when it says more than the title. */
  label?: string;
  description?: ReactNode;
  onClose: () => void;
  busy?: boolean;
  width?: string;
  cancelLabel: string;
  confirmLabel: string;
  onConfirm: () => void;
  confirmDisabled?: boolean;
  tone?: "danger" | "primary";
  children?: ReactNode;
}) {
  return (
    <CalendarSheet phone={phone} label={label ?? title} onClose={onClose} dismissable={!busy} width={width} flush grabber={false}>
      <div className="flex min-h-0 flex-col">
        <header className={cn("flex items-start justify-between gap-4", phone ? "px-5 pt-[22px]" : "px-6 pb-2 pt-6")}>
          <div className="flex min-w-0 flex-col gap-1.5">
            <h2 className="font-section text-[20px] font-extrabold leading-[1.1] tracking-[0.015em] text-co-text">{title}</h2>
            {description ? <p className="text-[13px] leading-[1.55] text-co-muted">{description}</p> : null}
          </div>
          <KitIconButton kind="close" icon={X} aria-label="Close" disabled={busy} onClick={onClose} />
        </header>
        {children ? <div className="flex min-h-0 flex-col gap-3 overflow-y-auto px-6 pb-4 pt-3">{children}</div> : null}
        <div className={cn("flex items-center justify-end", phone ? "gap-2.5 px-5 pb-5 pt-3" : "gap-3 px-6 pb-6 pt-4")}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className={cn(kitButtonClass({ variant: "ghost" }), phone && "h-12 flex-1")}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirmDisabled || busy}
            aria-busy={busy || undefined}
            className={cn(kitButtonClass({ variant: tone === "danger" ? "danger" : "primary" }), phone && "h-12 flex-1")}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </CalendarSheet>
  );
}

/** Calendar / Re-plan acknowledge row: the tick a destructive confirm waits for, on the recess. */
export function AcknowledgeRow({
  checked,
  onChange,
  disabled = false,
  children,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <KitCheckbox
      isSelected={checked}
      onChange={onChange}
      isDisabled={disabled}
      className="w-full gap-2.5 bg-co-recess px-3 py-2.5 text-[13.5px] text-co-text"
    >
      {children}
    </KitCheckbox>
  );
}
